// ─── Tax Calculation Engine (parameterized with engine settings) ───
// All functions accept config as parameter instead of using hardcoded constants

import {
  getApplicableMinimumWage,
  getLocalTaxEntry,
  computeLocalIncomeTax,
} from './jurisdictionResolution';

function calcBracketTax(income, brackets) {
  if (income <= 0 || !brackets || !brackets.length) return 0;
  let tax = 0;
  for (const b of brackets) {
    if (income <= b.min) break;
    tax += (Math.min(income, b.max === null ? Infinity : b.max) - b.min) * b.rate;
  }
  return tax;
}

// Map filing status to correct bracket set
// MFS uses Single brackets, QSS uses MFJ brackets
function getBracketSet(filing, config) {
  switch (filing) {
    case 'single':
    case 'mfs':
      return config.federalBrackets.single || config.federalBrackets.single;
    case 'married':
    case 'qss':
      return config.federalBrackets.married || config.federalBrackets.married;
    case 'hoh':
      return config.federalBrackets.hoh || config.federalBrackets.hoh;
    default:
      return config.federalBrackets.single;
  }
}

// Get pay periods per year (with per-employee override support)
function getPayPeriods(config, employeePayPeriods = null) {
  return employeePayPeriods || config.engineSettings?.payPeriodsPerYear || 12;
}

export function calcPerPeriodFederalTax(annual, filing, config, payPeriods = null) {
  const periods = getPayPeriods(config, payPeriods);
  const sd = config.standardDeduction[filing] || config.standardDeduction.single;
  const brackets = getBracketSet(filing, config);
  return calcBracketTax(Math.max(0, annual - sd), brackets) / periods;
}

export function calcPerPeriodStateTax(annual, stateCode, config, payPeriods = null) {
  const periods = getPayPeriods(config, payPeriods);
  const s = config.stateData[stateCode];
  return s?.rates?.length ? calcBracketTax(annual, s.rates) / periods : 0;
}

export function calcPerPeriodSSI(perPeriod, annual, config, payPeriods = null) {
  const periods = getPayPeriods(config, payPeriods);
  return annual > config.payrollConstants.ssWageBase
    ? (config.payrollConstants.ssWageBase * config.payrollConstants.ssRate) / periods
    : perPeriod * config.payrollConstants.ssRate;
}

export function calcPerPeriodMedicare(perPeriod, annual, filingStatus, config, payPeriods = null) {
  const periods = getPayPeriods(config, payPeriods);
  const baseMedicare = perPeriod * config.payrollConstants.medicareRate;
  
  // Additional Medicare Tax applies only to wages in EXCESS of the threshold
  const thresholds = config.payrollConstants.addlMedicareThresholds;
  const threshold = thresholds ? (thresholds[filingStatus] || 200000) : 200000;
  
  let additionalMedicare = 0;
  if (annual > threshold) {
    const excessAnnual = annual - threshold;
    const excessPerPeriod = excessAnnual / periods;
    additionalMedicare = excessPerPeriod * config.payrollConstants.addlMedicareRate;
  }
  
  return baseMedicare + additionalMedicare;
}

export function calcPerPeriodSDI(perPeriod, stateCode, config) {
  return perPeriod * (config.stateData[stateCode]?.sdi || 0);
}

export function calcPerPeriodPFML(perPeriod, stateCode, config) {
  const s = config.stateData[stateCode];
  if (!s) return 0;
  return perPeriod * ((s.pfml || 0) + (s.fli || 0));
}

// Employer FICA Savings Calculation
function calcEmployerFicaSavings(annualSalary, annualPremium, config, periods) {
  const method = config.engineSettings?.employerFicaMethod || 'simplified';
  
  if (method === 'simplified') {
    // Simplified: flat rate on full premium
    return (annualPremium * config.payrollConstants.employerFicaRate) / periods;
  } else {
    // Wage-Base Aware: account for SS wage base cap
    const ssWageBase = config.payrollConstants.ssWageBase;
    const ssRate = config.payrollConstants.ssRate;
    const medicareRate = config.payrollConstants.medicareRate;
    
    // SS savings (capped at wage base)
    const ssWithout = Math.min(annualSalary, ssWageBase) * ssRate;
    const ssWith = Math.min(annualSalary - annualPremium, ssWageBase) * ssRate;
    const ssSavings = ssWithout - ssWith;
    
    // Medicare savings (uncapped)
    const medicareSavings = annualPremium * medicareRate;
    
    return (ssSavings + medicareSavings) / periods;
  }
}

// Employer FUTA Savings Calculation
function calcEmployerFutaSavings(annualSalary, annualPremium, config, periods) {
  const futaWageBase = config.engineSettings?.futaWageBase || 7000;
  const futaRate = config.engineSettings?.futaRate || 0.006;
  
  const futaWithout = Math.min(annualSalary, futaWageBase) * futaRate;
  const futaWith = Math.min(Math.max(annualSalary - annualPremium, 0), futaWageBase) * futaRate;
  return (futaWithout - futaWith) / periods;
}


export function calculateScenario(annualSalary, stateCode, filingStatus, monthlyPremium, monthlyClaim, config, payPeriods = null, employeeContext = null) {
  // CRITICAL: All calculations are ANNUAL. Pay frequency is for DISPLAY ONLY.
  // Plan premiums are ALWAYS monthly amounts: Annual Premium = monthlyPremium × 12
  const periods = getPayPeriods(config, payPeriods);
  const perPeriodGross = annualSalary / periods;
  
  // Plan amounts are ALWAYS monthly - convert to annual
  const annualPremium = monthlyPremium * 12;
  const annualClaim = monthlyClaim * 12;
  
  const effectiveAnnual = annualSalary - annualPremium;
  const effectivePerPeriod = effectiveAnnual / periods;
  
  // Get conformity settings
  const state = config.stateData[stateCode];
  const conformsIncomeTax = state?.conformityIncomeTax !== false;
  const conformsSdi = state?.conformitySdi !== false;
  const conformsPfml = state?.conformityPfml !== false;

  // Phase 17: resolve local income tax entry for this employee, if any.
  // employeeContext = { city, zip, state } | null. When omitted (legacy callers),
  // we still attempt resolution from stateCode-only (no local match possible).
  const empForLookup = {
    city: employeeContext?.city || null,
    zip: employeeContext?.zip || null,
    state: stateCode,
  };
  const localTaxResolution = getLocalTaxEntry(empForLookup, config, employeeContext?.employer || null);
  const localEntry = localTaxResolution?.entry || null;

  // Helper: per-period local tax for a given annual income.
  const localTaxFor = (annual) => localEntry
    ? computeLocalIncomeTax(localEntry, annual, filingStatus) / periods
    : 0;

  // WITHOUT Section 125
  const w = {
    gross: perPeriodGross,
    federalTax: calcPerPeriodFederalTax(annualSalary, filingStatus, config, periods),
    socialSecurity: calcPerPeriodSSI(perPeriodGross, annualSalary, config, periods),
    medicare: calcPerPeriodMedicare(perPeriodGross, annualSalary, filingStatus, config, periods),
    stateTax: calcPerPeriodStateTax(annualSalary, stateCode, config, periods),
    sdi: calcPerPeriodSDI(perPeriodGross, stateCode, config),
    pfml: calcPerPeriodPFML(perPeriodGross, stateCode, config),
    localTax: localTaxFor(annualSalary),
  };
  w.totalTax = w.federalTax + w.socialSecurity + w.medicare + w.stateTax + w.sdi + w.pfml + w.localTax;
  w.netTakeHome = w.gross - w.totalTax;

  // WITH Section 125
  const h = {
    gross: perPeriodGross,
    premium: annualPremium / periods,
    claim: annualClaim / periods,
    taxableIncome: effectivePerPeriod,
    federalTax: calcPerPeriodFederalTax(effectiveAnnual, filingStatus, config, periods),
    socialSecurity: calcPerPeriodSSI(effectivePerPeriod, effectiveAnnual, config, periods),
    medicare: calcPerPeriodMedicare(effectivePerPeriod, effectiveAnnual, filingStatus, config, periods),
    stateTax: conformsIncomeTax ? calcPerPeriodStateTax(effectiveAnnual, stateCode, config, periods) : calcPerPeriodStateTax(annualSalary, stateCode, config, periods),
    sdi: conformsSdi ? calcPerPeriodSDI(effectivePerPeriod, stateCode, config) : calcPerPeriodSDI(perPeriodGross, stateCode, config),
    pfml: conformsPfml ? calcPerPeriodPFML(effectivePerPeriod, stateCode, config) : calcPerPeriodPFML(perPeriodGross, stateCode, config),
    // Local taxes: most local jurisdictions conform to federal pre-tax treatment (entry.conformsToFederal === true).
    localTax: localEntry?.conformsToFederal ? localTaxFor(effectiveAnnual) : localTaxFor(annualSalary),
  };
  h.totalTax = h.federalTax + h.socialSecurity + h.medicare + h.stateTax + h.sdi + h.pfml + h.localTax;
  h.netTakeHome = h.gross - h.totalTax - h.premium + h.claim;

  // DIFFERENCE
  const d = {
    federalTax: w.federalTax - h.federalTax,
    socialSecurity: w.socialSecurity - h.socialSecurity,
    medicare: w.medicare - h.medicare,
    stateTax: w.stateTax - h.stateTax,
    sdi: w.sdi - h.sdi,
    pfml: w.pfml - h.pfml,
    localTax: w.localTax - h.localTax,
  };
  d.totalTax = d.federalTax + d.socialSecurity + d.medicare + d.stateTax + d.sdi + d.pfml + d.localTax;
  d.netTakeHome = h.netTakeHome - w.netTakeHome;

  // Employer FICA Savings
  const employerFicaSavingsPerPeriod = calcEmployerFicaSavings(annualSalary, annualPremium, config, periods);
  
  // FUTA Savings (if enabled)
  let employerFutaSavingsPerPeriod = 0;
  if (config.engineSettings?.includeFutaSavings) {
    employerFutaSavingsPerPeriod = calcEmployerFutaSavings(annualSalary, annualPremium, config, periods);
  }

  return {
    without: w,
    with125: h,
    diff: d,
    employerFicaSavingsPerPeriod,
    employerFutaSavingsPerPeriod,
    employerFicaSavingsAnnual: employerFicaSavingsPerPeriod * periods,
    employerFutaSavingsAnnual: employerFutaSavingsPerPeriod * periods,
    // Phase 17: surface jurisdiction context for UI/audit/workforce summary
    localTaxJurisdiction: localTaxResolution
      ? { key: localTaxResolution.key, source: localTaxResolution.location?.source || null, conforms: !!localEntry?.conformsToFederal }
      : null,
    localTaxAnnualSavings: (d.localTax || 0) * periods,
  };
}

export function findBreakEvenSalary(stateCode, filingStatus, tier, config, payPeriods = null, employeeContext = null) {
  const periods = getPayPeriods(config, payPeriods);
  const minNetBenefit = config.engineSettings?.minEmployeeNetBenefit || 0;
  const annualHours = config.engineSettings?.annualHoursForMinWage || 2080;
  let lo = config.engineSettings?.breakEvenMinSalary || 15000;
  let hi = config.engineSettings?.breakEvenMaxSalary || 250000;

  // Phase 17: jurisdiction-aware floor
  const minWageInfo = getApplicableMinimumWage(employeeContext || { state: stateCode }, config, employeeContext?.employer || null);
  const minWage = Number(minWageInfo.applicable) || (config.payrollConstants?.minWageHourly || 7.25);

  for (let i = 0; i < 30; i++) {
    const mid = Math.round((lo + hi) / 2);
    // Plans are ALWAYS monthly - pass monthly amounts
    const calc = calculateScenario(mid, stateCode, filingStatus, tier.monthlyPremium, tier.monthlyClaim, config, periods, employeeContext);
    const annualPremium = tier.monthlyPremium * 12; // ALWAYS × 12
    const residual = mid - annualPremium;
    const meetsMinBenefit = calc.diff.netTakeHome >= minNetBenefit;
    const meetsMinWage = residual / annualHours >= minWage;
    if (meetsMinBenefit && meetsMinWage) hi = mid;
    else lo = mid;
  }
  return hi;
}

export function optimizeEmployee(annualSalary, stateCode, filingStatus, config, payPeriods = null, employeeContext = null) {
  const periods = getPayPeriods(config, payPeriods);
  // Filter out archived plans
  const planTiers = (config.planTiers || []).filter(tier => !tier.archived);
  const minNetBenefit = config.engineSettings?.minEmployeeNetBenefit || 0;
  const annualHours = config.engineSettings?.annualHoursForMinWage || 2080;
  const optimizationStrategy = config.engineSettings?.optimizationStrategy || 'employee';
  
  // Guardrails from employerPlanRules (if they exist)
  const maxPremiumPercent = config.employerPlanRules?.maxPremiumPercentOfSalary;
  const useSalaryFloors = config.employerPlanRules?.useSalaryFloorsPerTier;
  const salaryFloors = config.employerPlanRules?.salaryFloorPerTier || {};

  // Phase 17: jurisdiction-aware minimum wage. Falls back to legacy flat
  // federal wage when no jurisdiction config is present. Surfaced for audit.
  const empForJurisdiction = employeeContext || { state: stateCode };
  const minWageInfo = getApplicableMinimumWage(empForJurisdiction, config, employeeContext?.employer || null);
  const applicableMinWage = Number(minWageInfo.applicable) || (config.payrollConstants?.minWageHourly || 7.25);
  
  const results = planTiers.map((tier) => {
    // CRITICAL: tier.monthlyPremium and tier.monthlyClaim are ALWAYS monthly amounts
    // Annual premium = monthlyPremium × 12 (NOT × periods)
    const calc = calculateScenario(annualSalary, stateCode, filingStatus, tier.monthlyPremium, tier.monthlyClaim, config, periods, employeeContext);
    const annualPremium = tier.monthlyPremium * 12; // ALWAYS × 12
    const residual = annualSalary - annualPremium;
    const hourly = residual / annualHours;
    const netPositiveBenefit = calc.diff.netTakeHome >= minNetBenefit; // Use configurable threshold
    const wageOk = hourly >= applicableMinWage && residual > 0;
    
    // Guardrail checks
    let passesGuardrails = true;
    let guardrailViolations = [];
    
    // Max premium % of salary guardrail
    if (maxPremiumPercent !== undefined && maxPremiumPercent !== null) {
      const premiumPercent = (annualPremium / annualSalary) * 100;
      if (premiumPercent > maxPremiumPercent) {
        passesGuardrails = false;
        guardrailViolations.push(`Premium ${premiumPercent.toFixed(1)}% exceeds max ${maxPremiumPercent}%`);
      }
    }
    
    // Salary floor per tier guardrail
    if (useSalaryFloors && salaryFloors[tier.name]) {
      const requiredSalary = salaryFloors[tier.name];
      if (annualSalary < requiredSalary) {
        passesGuardrails = false;
        guardrailViolations.push(`Salary below tier minimum $${requiredSalary.toLocaleString()}`);
      }
    }
    
    return {
      ...tier,
      calc,
      residual,
      hourly,
      employeePerPeriodBenefit: calc.diff.netTakeHome,
      employeeAnnualBenefit: calc.diff.netTakeHome * periods,
      employerPerPeriodFica: calc.employerFicaSavingsPerPeriod,
      employerAnnualFica: calc.employerFicaSavingsAnnual,
      employerPerPeriodFuta: calc.employerFutaSavingsPerPeriod,
      employerAnnualFuta: calc.employerFutaSavingsAnnual,
      hcPerPeriodRevenue: tier.spread / periods, // Display spread per period
      hcAnnualRevenue: tier.spread * 12, // ALWAYS × 12
      isViable: netPositiveBenefit && wageOk && passesGuardrails,
      isNegative: !netPositiveBenefit,
      wageOk,
      passesGuardrails,
      guardrailViolations,
    };
  });

  const viable = results.filter((r) => r.isViable);
  const hasViable = viable.length > 0;
  
  // Select optimal based on optimization strategy
  let optimal = null;
  if (hasViable) {
    if (optimizationStrategy === 'employer') {
      // Maximize employer FICA savings
      optimal = viable.reduce((b, r) => r.employerAnnualFica > b.employerAnnualFica ? r : b);
    } else if (optimizationStrategy === 'balanced') {
      // 50/50 weighted score
      optimal = viable.reduce((b, r) => {
        const rScore = (r.employeeAnnualBenefit * 0.5) + (r.employerAnnualFica * 0.5);
        const bScore = (b.employeeAnnualBenefit * 0.5) + (b.employerAnnualFica * 0.5);
        return rScore > bScore ? r : b;
      });
    } else {
      // Default: maximize employee benefit
      optimal = viable.reduce((b, r) => r.employeeAnnualBenefit > b.employeeAnnualBenefit ? r : b);
    }
  } else {
    optimal = results[results.length - 1]; // Fallback to highest tier
  }

  let breakEvenSalary = null;
  if (!hasViable) {
    breakEvenSalary = findBreakEvenSalary(stateCode, filingStatus, planTiers[planTiers.length - 1], config, periods, employeeContext);
  }

  return { results, optimal, hasViable, breakEvenSalary, minimumWageInfo: minWageInfo, applicableMinWage };
}

// ─── Eligibility-Aware Employee Optimization ───
/**
 * Optimize employee with eligibility screening
 * @param {Object} employee - Employee data from CSV (includes eligibility fields)
 * @param {Object} config - Full config including employerPlanRules
 * @returns {Object} - { ...optimizationResults, eligibility, employee }
 */
export function optimizeEmployeeWithEligibility(employee, config) {
  const { checkEligibility } = require('./eligibility');
  
  // Step 1: Run initial eligibility checks (before financial optimization)
  // This checks worker type, ownership, hours, waiting period, union
  const initialEligibility = checkEligibility(employee, config.employerPlanRules, null);
  
  // If already ineligible due to non-financial reasons, return early
  if (!initialEligibility.eligible && initialEligibility.status !== 'salary') {
    return {
      ...employee,
      eligibility: initialEligibility,
      results: [],
      optimal: null,
      hasViable: false,
      breakEvenSalary: null,
    };
  }
  
  // Step 2: Run financial optimization
  // Defensive: `employee.payFrequency` MUST be numeric (52/26/24/12/1). If a
  // transformer (or legacy upload) accidentally writes a string like
  // 'biweekly', `salary / 'biweekly'` becomes NaN downstream and every plan
  // becomes non-viable -> employee is wrongly marked "salary too low". Coerce
  // and fall back to the configured default.
  const PERIODS_BY_NAME = { weekly: 52, biweekly: 26, 'bi-weekly': 26, semimonthly: 24, 'semi-monthly': 24, monthly: 12, annual: 1, annually: 1 };
  let normalizedPayPeriods = employee.payFrequency;
  if (typeof normalizedPayPeriods === 'string') {
    normalizedPayPeriods = PERIODS_BY_NAME[normalizedPayPeriods.trim().toLowerCase()] || null;
  }
  const payPeriods = (typeof normalizedPayPeriods === 'number' && normalizedPayPeriods > 0)
    ? normalizedPayPeriods
    : (config.engineSettings.payPeriodsPerYear || 12);
  // Phase 17: pass jurisdiction context (city/zip) so the engine can apply
  // the correct local income tax and city/state minimum wage.
  const employeeContext = {
    city: employee.city || null,
    zip: employee.zip || null,
    state: employee.state,
    employer: config?._employerContext || null, // set by caller when employer HQ should be used as a fallback
  };
  const optimization = optimizeEmployee(
    employee.salary,
    employee.state,
    employee.filing,
    config,
    payPeriods,
    employeeContext
  );
  
  // Step 3: Run final eligibility check with financial viability
  const finalEligibility = checkEligibility(employee, config.employerPlanRules, optimization.optimal);
  
  return {
    ...employee,
    eligibility: finalEligibility,
    ...optimization,
  };
}

// ─── CSV Parser ───
export function parseCSV(text, stateData) {
  const lines = text.trim().split(/\r?\n/);
  if (lines.length < 2) return [];
  const headers = lines[0].split(",").map((h) => h.trim().replace(/^["']|["']$/g, "").toLowerCase());
  const pats = {
    name: /^(name|employee.?name|full.?name|emp.?name)$/i,
    salary: /^(salary|annual.?salary|gross.?salary|annual.?pay|gross.?pay|gross.?income|yearly.?salary|pay|income|wage|compensation)$/i,
    state: /^(state|work.?state|st|state.?code|location)$/i,
    filing: /^(filing|filing.?status|tax.?status|status|marital)$/i,
    payFrequency: /^(pay.?frequency|pay.?period|pay.?schedule|frequency|periods)$/i,
    annualHours: /^(annual.?hours|hours|work.?hours|annual.?work.?hours)$/i,
    employmentType: /^(employment.?type|type|status|ft.?pt|employment.?status)$/i,
    id: /^(id|emp.?id|employee.?id|emp_id|badge)$/i,
    department: /^(department|dept|division|team|cost.?center)$/i,
    // NEW ELIGIBILITY COLUMNS
    hireDate: /^(hire.?date|start.?date|date.?of.?hire|doh|employment.?date)$/i,
    weeklyHours: /^(weekly.?hours|hours.?week|avg.?hours|hours.?per.?week|weekly.?hrs)$/i,
    workerType: /^(worker.?type|employee.?type|classification|w2.?1099|worker.?classification)$/i,
    ownership: /^(ownership|owner.?%|equity|ownership.?percentage|ownership.?percent)$/i,
    hce: /^(hce|highly.?compensated|hce.?flag|hce.?status)$/i,
    union: /^(union|union.?member|cba|collective.?bargaining|union.?status)$/i,
    email: /^(email|email.?address|e.?mail|work.?email|employee.?email|contact.?email)$/i,
    firstName: /^(first.?name|fname|given.?name|first)$/i,
    lastName: /^(last.?name|lname|surname|family.?name|last)$/i,
    city: /^(city|home.?city|residence.?city|work.?city|employee.?city)$/i,
    zip: /^(zip|zip.?code|postal.?code|postal|home.?zip)$/i,
  };
  const m = {};
  headers.forEach((h, i) => {
    for (const [f, p] of Object.entries(pats)) {
      if (!m[f] && p.test(h)) m[f] = i;
    }
  });
  if (m.salary === undefined) return [];

  const filingMap = {
    s: "single", single: "single",
    m: "married", married: "married", mfj: "married", "married filing jointly": "married",
    mfs: "mfs", "married filing separately": "mfs",
    h: "hoh", hoh: "hoh", "head of household": "hoh",
    qss: "qss", "qualifying surviving spouse": "qss", "qualifying widow": "qss", "qualifying widower": "qss", "surviving spouse": "qss"
  };
  
  const payFrequencyMap = {
    'monthly': 12, 'month': 12, 'mo': 12, '12': 12,
    'semi-monthly': 24, 'semimonthly': 24, 'semi': 24, '24': 24,
    'biweekly': 26, 'bi-weekly': 26, 'biweek': 26, '26': 26,
    'weekly': 52, 'week': 52, 'wk': 52, '52': 52,
  };
  
  const employmentTypeMap = {
    'full-time': 2080, 'fulltime': 2080, 'ft': 2080, 'full': 2080,
    'part-time': 1040, 'parttime': 1040, 'pt': 1040, 'part': 1040,
  };
  
  const stateMap = {};
  Object.entries(stateData).forEach(([c, s]) => {
    stateMap[c.toLowerCase()] = c;
    stateMap[s.name.toLowerCase()] = c;
  });

  return lines.slice(1).map((line, idx) => {
    const cols = line.split(",").map((c) => c.trim().replace(/^["']|["']$/g, ""));
    const sal = parseFloat((cols[m.salary] || "").replace(/[\$,\s]/g, ""));
    if (isNaN(sal) || sal <= 0) return null;
    const rawSt = (cols[m.state] || "").trim();
    const rawFiling = (cols[m.filing] || "single").trim().toLowerCase();
    const rawPayFreq = m.payFrequency !== undefined && cols[m.payFrequency] !== undefined ? (cols[m.payFrequency] || "").trim().toLowerCase() : null;
    const rawAnnualHours = m.annualHours !== undefined && cols[m.annualHours] !== undefined ? parseFloat(cols[m.annualHours]) : null;
    const rawEmploymentType = m.employmentType !== undefined && cols[m.employmentType] !== undefined ? (cols[m.employmentType] || "").trim().toLowerCase() : null;
    
    // Determine annual hours: explicit value > employment type > null (use default)
    let annualHours = null;
    if (rawAnnualHours && !isNaN(rawAnnualHours)) {
      annualHours = rawAnnualHours;
    } else if (rawEmploymentType && employmentTypeMap[rawEmploymentType]) {
      annualHours = employmentTypeMap[rawEmploymentType];
    }
    
    // Parse NEW eligibility fields
    // Hire Date - accept multiple formats
    let hireDate = null;
    if (m.hireDate !== undefined && cols[m.hireDate] !== undefined) {
      const rawDate = cols[m.hireDate].trim();
      if (rawDate) {
        // Try parsing common formats: MM/DD/YYYY, YYYY-MM-DD, MM-DD-YYYY
        const parsed = new Date(rawDate);
        if (!isNaN(parsed.getTime())) {
          hireDate = parsed;
        }
      }
    }
    
    // Weekly Hours - default 40 if missing
    let weeklyHours = 40;
    if (m.weeklyHours !== undefined && cols[m.weeklyHours] !== undefined) {
      const parsed = parseFloat(cols[m.weeklyHours]);
      if (!isNaN(parsed) && parsed > 0) {
        weeklyHours = parsed;
      }
    }
    
    // Worker Type - default W-2 if missing
    let workerType = 'W-2';
    if (m.workerType !== undefined && cols[m.workerType] !== undefined) {
      const raw = cols[m.workerType].trim().toLowerCase();
      if (raw.includes('1099') || raw.includes('contractor') || raw.includes('independent')) {
        workerType = '1099';
      }
    }
    
    // Ownership Percentage - default 0 if missing
    let ownership = 0;
    if (m.ownership !== undefined && cols[m.ownership] !== undefined) {
      const parsed = parseFloat(cols[m.ownership].replace(/%/g, ''));
      if (!isNaN(parsed)) {
        ownership = parsed;
      }
    }
    
    // HCE Flag - default null (calculated from salary) if missing
    let hceOverride = null;
    if (m.hce !== undefined && cols[m.hce] !== undefined) {
      const raw = cols[m.hce].trim().toLowerCase();
      if (raw === 'yes' || raw === 'y' || raw === 'true' || raw === '1') {
        hceOverride = true;
      } else if (raw === 'no' || raw === 'n' || raw === 'false' || raw === '0') {
        hceOverride = false;
      }
    }
    
    // Union - default false if missing
    let isUnion = false;
    if (m.union !== undefined && cols[m.union] !== undefined) {
      const raw = cols[m.union].trim().toLowerCase();
      if (raw === 'yes' || raw === 'y' || raw === 'true' || raw === '1') {
        isUnion = true;
      }
    }
    
    // Email — optional column. When the CSV has separate firstName/lastName
    // columns, prefer those; otherwise fall back to the single `name` column.
    let emailVal = null;
    if (m.email !== undefined && cols[m.email] !== undefined) {
      const raw = (cols[m.email] || '').trim();
      if (raw && raw.includes('@')) emailVal = raw;
    }
    let firstName = null;
    let lastName = null;
    if (m.firstName !== undefined && cols[m.firstName] !== undefined) {
      firstName = (cols[m.firstName] || '').trim() || null;
    }
    if (m.lastName !== undefined && cols[m.lastName] !== undefined) {
      lastName = (cols[m.lastName] || '').trim() || null;
    }
    // If we have a `name` column, build firstName/lastName from it (best-effort
    // split on first space) — keeps both single-name AND first/last CSV layouts
    // round-trip correctly, and lets the Enrollment step display either form.
    let combinedName = (cols[m.name] || `Employee ${idx + 1}`).trim();
    if (!firstName && !lastName && combinedName) {
      const parts = combinedName.split(/\s+/);
      if (parts.length >= 2) {
        firstName = parts[0];
        lastName = parts.slice(1).join(' ');
      } else {
        firstName = combinedName;
      }
    } else if (firstName || lastName) {
      // Synthesise a `name` for downstream code that expects it.
      combinedName = `${firstName || ''} ${lastName || ''}`.trim() || combinedName;
    }

    return {
      row: idx + 2,
      name: combinedName,
      firstName: firstName,
      lastName: lastName,
      email: emailVal,
      salary: sal,
      state: stateMap[rawSt.toLowerCase()] || "CA",
      filing: filingMap[rawFiling] || "single",
      payFrequency: rawPayFreq ? (payFrequencyMap[rawPayFreq] || null) : null,
      annualHours: annualHours,
      employeeId: m.id !== undefined ? cols[m.id] : null,
      department: m.department !== undefined ? cols[m.department] : null,
      // NEW eligibility fields
      hireDate: hireDate,
      weeklyHours: weeklyHours,
      workerType: workerType,
      ownership: ownership,
      hceOverride: hceOverride,
      isUnion: isUnion,
      // Phase 17: location for jurisdiction-aware min wage + local income tax
      city: m.city !== undefined && cols[m.city] !== undefined ? (cols[m.city] || '').trim() || null : null,
      zip: m.zip !== undefined && cols[m.zip] !== undefined ? (cols[m.zip] || '').trim().slice(0, 5) || null : null,
    };
  }).filter(Boolean);
}
